import {
  createTheme,
  PaletteMode,
  PaletteColorOptions
} from '@mui/material/styles'

// Extend Material-UI theme types to include custom colors
declare module '@mui/material/styles' {
  interface PaletteOptions {
    outdatedCritical?: PaletteColorOptions
    outdated?: PaletteColorOptions
    defaultComponent?: PaletteColorOptions
    primarySoft?: PaletteColorOptions
    secondarySoft?: PaletteColorOptions
    tabBorder?: PaletteColorOptions
    examCard?: PaletteColorOptions
    ecgStroke?: PaletteColorOptions
    ecgGridThin?: PaletteColorOptions
    ecgGridThick?: PaletteColorOptions
    ecgAlert?: PaletteColorOptions
    ecgNormal?: PaletteColorOptions
    vitalSignHeartRate?: PaletteColorOptions
    vitalSignO2Saturation?: PaletteColorOptions
    vitalSignBloodPressure?: PaletteColorOptions
    hdaWarning?: PaletteColorOptions
    painSliderLow?: PaletteColorOptions
    painSliderYellow?: PaletteColorOptions
    painSliderMid?: PaletteColorOptions
    painSliderOrange?: PaletteColorOptions
    painSliderHigh?: PaletteColorOptions
    selectedBorder?: PaletteColorOptions
    defaultBorder?: PaletteColorOptions
  }

  interface Palette {
    outdatedCritical: Palette['primary']
    outdated: Palette['primary']
    defaultComponent: Palette['primary']
    primarySoft: Palette['primary']
    secondarySoft: Palette['primary']
    tabBorder: Palette['primary']
    examCard: Palette['primary']
    ecgStroke: Palette['primary']
    ecgGridThin: Palette['primary']
    ecgGridThick: Palette['primary']
    ecgAlert: Palette['primary']
    ecgNormal: Palette['primary']
    vitalSignHeartRate: Palette['primary']
    vitalSignO2Saturation: Palette['primary']
    vitalSignBloodPressure: Palette['primary']
    hdaWarning: Palette['primary']
    painSliderLow: Palette['primary']
    painSliderYellow: Palette['primary']
    painSliderMid: Palette['primary']
    painSliderOrange: Palette['primary']
    painSliderHigh: Palette['primary']
    selectedBorder: Palette['primary']
    defaultBorder: Palette['primary']
  }
}

interface ColorModeConfig {
  primary: string
  ecgStroke: string
  ecgGridThin: string
  ecgGridThick: string
  ecgAlert: string
  ecgNormal: string
  vitalSignHeartRate: string
  vitalSignO2Saturation: string
  vitalSignBloodPressure: string
  hdaWarning: string
  error: string
  outdatedCritical: string
  outdated: string
  defaultComponent: string
  tabBorder: string
  backgroundPaper: string
  backgroundDefault: string
  textPrimary: string
  textSecondary: string
  contrastTextDark: string
  contrastTextLight: string
  shadowColor: string
  painSliderLow: string
  painSliderYellow: string
  painSliderMid: string
  painSliderOrange: string
  painSliderHigh: string
  selectedBorder: string
  defaultBorder: string
}

// Brand primary color for light mode (dark blue)
const primaryLight = '#4d6b8d'
// Brand primary color for dark mode (lighter blue, better contrast on dark backgrounds)
const primaryDark = '#7aafd4'
const secondaryColor = '#6B7280'

const getShadow = (
  baseColor: string,
  opacity1: number = 0.2,
  opacity2: number = 0.14,
  opacity3: number = 0.12
) => {
  // Extract RGB values from rgba color
  const rgbMatch = /rgba?\((\d+),\s*(\d+),\s*(\d+)/.exec(baseColor)
  if (rgbMatch) {
    const [, r, g, b] = rgbMatch
    return `0px 2px 1px -1px rgba(${r}, ${g}, ${b}, ${opacity1}), 0px 1px 1px 0px rgba(${r}, ${g}, ${b}, ${opacity2}), 0px 1px 3px 0px rgba(${r}, ${g}, ${b}, ${opacity3})`
  }
  // Fallback for non-rgba colors
  return `0px 2px 1px -1px ${baseColor}, 0px 1px 1px 0px ${baseColor}, 0px 1px 3px 0px ${baseColor}`
}

// Color configurations for each mode
const colorModes: Record<PaletteMode, ColorModeConfig> = {
  light: {
    primary: primaryLight,
    ecgStroke: '#000000',
    ecgGridThin: 'rgba(255, 180, 180, 0.2)',
    ecgGridThick: 'rgba(255, 120, 120, 0.4)',
    ecgAlert: '#FF0000',
    ecgNormal: '#00AA00',
    vitalSignHeartRate: '#8C4D6B',
    vitalSignO2Saturation: '#4D6B8C',
    vitalSignBloodPressure: '#5e3a4b',
    hdaWarning: 'rgba(218, 204, 62, 1.0)',
    error: 'rgb(211, 47, 47)',
    outdatedCritical: 'rgb(255, 221, 207)',
    outdated: 'rgb(255, 244, 229)',
    defaultComponent: 'rgba(247, 247, 247)',
    tabBorder: 'rgb(245,245,245)',
    backgroundPaper: '#fafafa',
    backgroundDefault: '#ffffff',
    textPrimary: 'rgba(0, 0, 0, 0.87)',
    textSecondary: 'rgba(0, 0, 0, 0.6)',
    contrastTextDark: '#000',
    contrastTextLight: '#fff',
    shadowColor: 'rgba(0, 0, 0, 0.2)',
    painSliderLow: '#2E7D32',
    painSliderYellow: '#9acd32',
    painSliderMid: '#FF9800',
    painSliderOrange: '#ff6347',
    painSliderHigh: '#D32F2F',
    selectedBorder: '#FF6B35',
    defaultBorder: '#e0e0e0'
  },
  dark: {
    primary: primaryDark,
    ecgStroke: '#00FFFF',
    ecgGridThin: 'rgba(80, 100, 120, 0.4)',
    ecgGridThick: 'rgba(80, 100, 120, 0.7)',
    ecgAlert: '#FF6B35',
    ecgNormal: '#00FF88',
    vitalSignHeartRate: '#FF7BAE',
    vitalSignO2Saturation: '#8BB5FF',
    vitalSignBloodPressure: '#C785A0',
    hdaWarning: 'rgba(238, 224, 92, 1.0)',
    error: 'rgb(255, 109, 99)',
    outdatedCritical: 'rgb(123, 71, 49)',
    outdated: 'rgb(133, 109, 73)',
    defaultComponent: 'rgb(28, 28, 28)',
    tabBorder: 'rgb(45, 45, 45)',
    backgroundPaper: 'rgb(45, 45, 45)',
    backgroundDefault: '#1e1e1e',
    textPrimary: '#ffffff',
    textSecondary: 'rgba(255, 255, 255, 0.7)',
    contrastTextDark: '#000',
    contrastTextLight: '#fff',
    shadowColor: 'rgba(255, 255, 255, 0.15)',
    painSliderLow: '#4CAF50',
    painSliderYellow: '#B2DF3A',
    painSliderMid: '#FFA726',
    painSliderOrange: '#FF7F7F',
    painSliderHigh: '#EF5350',
    selectedBorder: '#FF6B35',
    defaultBorder: 'rgba(255, 255, 255, 0.12)'
  }
}

/**
 * createAppTheme creates a Material UI theme with light or dark mode palette.
 * Components should consume colors via theme.palette rather than hardcoded values.
 * @param {PaletteMode} mode - 'light' or 'dark'
 * @returns {Theme} - The Material UI theme object
 */
export const createAppTheme = (mode: PaletteMode) => {
  const colors = colorModes[mode]

  return createTheme({
    palette: {
      mode,
      primarySoft: {
        main: colors.primary,
        contrastText: '#fff'
      },
      secondarySoft: {
        main: secondaryColor,
        contrastText: '#fff'
      },
      ecgStroke: {
        main: colors.ecgStroke,
        contrastText:
          mode === 'dark' ? colors.contrastTextDark : colors.contrastTextLight
      },
      ecgGridThin: {
        main: colors.ecgGridThin,
        contrastText:
          mode === 'dark' ? colors.contrastTextDark : colors.contrastTextLight
      },
      ecgGridThick: {
        main: colors.ecgGridThick,
        contrastText:
          mode === 'dark' ? colors.contrastTextDark : colors.contrastTextLight
      },
      ecgAlert: {
        main: colors.ecgAlert,
        contrastText: '#fff'
      },
      ecgNormal: {
        main: colors.ecgNormal,
        contrastText:
          mode === 'dark' ? colors.contrastTextDark : colors.contrastTextLight
      },
      vitalSignHeartRate: {
        main: colors.vitalSignHeartRate,
        contrastText: '#fff'
      },
      vitalSignO2Saturation: {
        main: colors.vitalSignO2Saturation,
        contrastText: '#fff'
      },
      vitalSignBloodPressure: {
        main: colors.vitalSignBloodPressure,
        contrastText: '#fff'
      },
      hdaWarning: {
        main: colors.hdaWarning,
        contrastText:
          mode === 'dark' ? colors.contrastTextDark : colors.contrastTextLight
      },
      error: {
        main: colors.error
      },
      outdatedCritical: {
        main: colors.outdatedCritical,
        contrastText:
          mode === 'dark' ? colors.contrastTextLight : colors.contrastTextDark
      },
      outdated: {
        main: colors.outdated,
        contrastText:
          mode === 'dark' ? colors.contrastTextLight : colors.contrastTextDark
      },
      defaultComponent: {
        main: colors.defaultComponent,
        contrastText:
          mode === 'dark' ? colors.contrastTextLight : colors.contrastTextDark
      },
      tabBorder: {
        main: colors.tabBorder
      },
      painSliderLow: {
        main: colors.painSliderLow,
        contrastText: '#fff'
      },
      painSliderYellow: {
        main: colors.painSliderYellow,
        contrastText: '#fff'
      },
      painSliderMid: {
        main: colors.painSliderMid,
        contrastText: '#fff'
      },
      painSliderOrange: {
        main: colors.painSliderOrange,
        contrastText: '#fff'
      },
      painSliderHigh: {
        main: colors.painSliderHigh,
        contrastText: '#fff'
      },
      selectedBorder: {
        main: colors.selectedBorder,
        contrastText: '#fff'
      },
      defaultBorder: {
        main: colors.defaultBorder,
        contrastText: colors.textPrimary
      },
      background: {
        paper: colors.backgroundPaper,
        default: colors.backgroundDefault
      },
      text: {
        primary: colors.textPrimary,
        secondary: colors.textSecondary
      }
    },
    shadows: [
      'none',
      getShadow(colors.shadowColor, 0.2, 0.14, 0.12),
      getShadow(colors.shadowColor, 0.2, 0.14, 0.12),
      getShadow(colors.shadowColor, 0.2, 0.14, 0.12),
      getShadow(colors.shadowColor, 0.2, 0.14, 0.12),
      getShadow(colors.shadowColor, 0.2, 0.14, 0.12),
      getShadow(colors.shadowColor, 0.2, 0.14, 0.12),
      getShadow(colors.shadowColor, 0.2, 0.14, 0.12),
      getShadow(colors.shadowColor, 0.2, 0.14, 0.12),
      getShadow(colors.shadowColor, 0.2, 0.14, 0.12),
      getShadow(colors.shadowColor, 0.2, 0.14, 0.12),
      getShadow(colors.shadowColor, 0.2, 0.14, 0.12),
      getShadow(colors.shadowColor, 0.2, 0.14, 0.12),
      getShadow(colors.shadowColor, 0.2, 0.14, 0.12),
      getShadow(colors.shadowColor, 0.2, 0.14, 0.12),
      getShadow(colors.shadowColor, 0.2, 0.14, 0.12),
      getShadow(colors.shadowColor, 0.2, 0.14, 0.12),
      getShadow(colors.shadowColor, 0.2, 0.14, 0.12),
      getShadow(colors.shadowColor, 0.2, 0.14, 0.12),
      getShadow(colors.shadowColor, 0.2, 0.14, 0.12),
      getShadow(colors.shadowColor, 0.2, 0.14, 0.12),
      getShadow(colors.shadowColor, 0.2, 0.14, 0.12),
      getShadow(colors.shadowColor, 0.2, 0.14, 0.12),
      getShadow(colors.shadowColor, 0.2, 0.14, 0.12),
      getShadow(colors.shadowColor, 0.2, 0.14, 0.12)
    ],
    components: {
      MuiBadge: {
        styleOverrides: {
          colorInfo: ({ theme }) => ({
            color: theme.palette.info.contrastText,
            fontWeight: 'bold'
          })
        }
      }
    }
  })
}
